'use client';
import React from 'react';
import styles from '../style.module.scss';
import Image from 'next/image';
import { IProduct } from '@mybestbrands/types';
import { useTranslation } from 'next-i18next';
import ClickOutCTA from '@/app/ui/components/clickOutCTA';
import { PrefetchOnHoverLink } from '@/app/ui/components/prefetchonhoverlink';
import ColorBoxOverlay from '@/app/ui/components/list/listitem/listitemcolorbox';
import { trackSSTEvent } from '@/app/utils/tracking';

interface ProductCardOverlayProps {
  onClose: () => void;
  item: IProduct;
  detailsUrl: string;
  locale: string;
  groupProducts?: IProduct[];
  trackingData?: object | null;
}

const MAX_VISIBLE_SIZES = 5;

const ProductCardOverlay = ({
  onClose,
  item,
  detailsUrl,
  locale,
  groupProducts,
  trackingData,
}: ProductCardOverlayProps) => {
  const { t } = useTranslation(['common']);
  const colorVariantItems = groupProducts?.filter((el) => !/(\..*){2,}/.test(el.productId || ''));
  const couponCode = item.priorityCoupon?.code || '';
  const hasColors = !!(colorVariantItems && colorVariantItems.length > 1);
  const couponCodeCheck = (code: string): boolean => code.length > 0 && !code.includes(' ');

  const handleOverlayClick = (event: React.MouseEvent) => {
    // Prevent the click event from bubbling up to the parent element
    event.stopPropagation();
  };

  const getShippingClass = () => {
    if (couponCodeCheck(item.priorityCoupon?.code || '')) {
      return hasColors ? styles['couponColorsApplied'] : styles['couponApplied'];
    } else {
      return hasColors ? styles['colorsApplied'] : styles['noColorsOrCouponApplied'];
    }
  };

  const renderShippingCost = () => {
    const shippingClass = getShippingClass();

    return item?.shippingCost === null ? (
      <div className={`${shippingClass} ${styles['title']}`}>{t('POSSIBLE_SHIPPING_COST')}</div>
    ) : item.shippingCost === 0 ? (
      <div className={`${shippingClass} ${styles['title']}`}>{t('FREE_SHIPPING')}</div>
    ) : (
      <>
        <div className={`${shippingClass} ${styles['title']}`}>
          {`${t('SHIPPING')}:`}
          <span className={`${styles['size']}`}>{`${item.shippingCost.centsToEuros()} €`}</span>
        </div>
      </>
    );
  };
  const renderSizes = () => {
    return (
      <>
        {item?.sizes && item?.sizes.length > 0 && <div className={styles['title']}>{t('AVAILABLE_SIZES')}</div>}
        <div className={styles['sizesWrapper']}>
          {item.sizes?.slice(0, MAX_VISIBLE_SIZES).map((size) => (
            <span key={size.id} className={styles['size']} style={{ borderBottom: '1px solid #ffffff' }}>
              {size.name}
            </span>
          ))}
        </div>
        {item.sizes && item.sizes.length > MAX_VISIBLE_SIZES && (
          <div className={styles['moreSizes']}>{t('MORE_SIZES')}</div>
        )}
      </>
    );
  };
  const renderCouponCode = () => {
    return (
      <>
        <div className={styles['quickViewCoupon']}>
          <div className={`${styles['title']}`}>{`${t('COUPON_CODE')}:`}</div>
          <span className={`${styles['size']}`}>{couponCode}</span>
        </div>
        <div
          className={`mb-3 px-1 ${styles['moreSizes']}`}
          style={hasColors ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}}>
          {t('CHECK_CONDITIONS_HERE')}
        </div>
      </>
    );
  };
  return (
    <div className={styles['overlay']} onClick={handleOverlayClick}>
      <div className={styles['closeBtn']} onClick={onClose}>
        <Image
          width={12}
          height={12}
          className={'cursor-pointer'}
          src="/intstatic/icons/close-x-white.svg"
          alt="close"
        />
      </div>
      <div className={styles['overlayContent']}>
        <ClickOutCTA product={item} locale={locale}>
          <div
            onClick={() => {
              trackSSTEvent('quick_view_cta_area_click', trackingData || null);
            }}>
            {renderSizes()}
            {colorVariantItems && colorVariantItems.length > 1 && (
              <ColorBoxOverlay colorVariantItems={colorVariantItems} />
            )}
            {renderShippingCost()}
            {couponCodeCheck(couponCode) && renderCouponCode()}
          </div>
        </ClickOutCTA>
        {detailsUrl && (
          <PrefetchOnHoverLink
            href={detailsUrl}
            title={`${item.brandName} ${item.name}`}
            rel={'follow'}
            onClick={() => {
              trackSSTEvent('quick_view_pdp_link_click', trackingData || null);
              trackSSTEvent('select_item', trackingData || null);
            }}>
            <div className={`text-white ${styles['details']} ${!item.hasCoupon || (!couponCode && 'mt-3')}`}>
              {' '}
              {t('MORE_DETAILS')}
            </div>
          </PrefetchOnHoverLink>
        )}
        <ClickOutCTA product={item} locale={locale}>
          <div
            className={styles['shopBtn']}
            data-testid="shop-button"
            onClick={() => {
              trackSSTEvent('quick_view_cta_button_click', trackingData || null);
            }}>
            {t('TO').toUpperCase()}
            <b className="ms-1">{item.retailerName.toUpperCase()}</b>
          </div>
        </ClickOutCTA>
      </div>
    </div>
  );
};

export default ProductCardOverlay;
